import axios from 'axios';
import { tokenStorage } from '../utils/tokenStorage';

const API_BASE_URL = import.meta.env.VITE_API_GATEWAY_URL || '';

// Create axios instance with auth interceptor
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

// Add auth token to all requests
apiClient.interceptors.request.use((config) => {
  const token = tokenStorage.getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface ImageDimensions {
  width: number;
  height: number;
}

export interface ImageMetadata {
  id: string;
  caseId: string;
  documentId: string;
  filename: string;
  pageNumber?: number;
  extractionMethod: 'embedded' | 'standalone';
  format: string;
  dimensions: ImageDimensions;
  aiAnalysis?: string;
  aiAnalysisTimestamp?: string;
  url: string;
  thumbnailUrl: string;
  createdAt: string;
}

// Module-level tracking to prevent double-triggering image extraction
export const extractionTriggeredCases = new Set<string>();

// In-memory cache: URL → { blob, objectUrl } to avoid re-downloading the same image
const blobCache = new Map<string, { blob: Blob; refCount: number }>();
// Track in-flight requests to deduplicate concurrent fetches for the same URL
const inflightRequests = new Map<string, Promise<Blob>>();

class ImageApi {
  /**
   * Get all images for a case
   */
  async getCaseImages(caseId: string): Promise<ImageMetadata[]> {
    const response = await apiClient.get(`/api/documents/cases/${caseId}/images`);
    return response.data;
  }

  /**
   * Trigger image extraction for all PDFs in a case
   */
  async triggerImageExtraction(caseId: string): Promise<void> {
    await apiClient.post(`/api/processing/images/extract-for-case/${caseId}`);
  }

  /**
   * Check extraction status for a case
   */
  async checkExtractionStatus(caseId: string): Promise<{
    caseId: string;
    hasExtractedImages: boolean;
    hasPdfsToProcess: boolean;
    extractionNeeded: boolean;
  }> {
    const response = await apiClient.get(`/api/processing/images/extraction-status/${caseId}`);
    return response.data;
  }

  /**
   * Get count of extracted images for a case
   */
  async getExtractedImageCount(caseId: string): Promise<number> {
    const response = await apiClient.get(`/api/extracted-images/case/${caseId}/count`);
    return response.data;
  }

  /**
   * Fetch an image as a blob using authenticated request, with dedup + caching.
   */
  async fetchImageBlob(url: string): Promise<Blob> {
    const path = url.startsWith('http') ? new URL(url).pathname : url;

    const cached = blobCache.get(path);
    if (cached) {
      cached.refCount++;
      return cached.blob;
    }

    const inflight = inflightRequests.get(path);
    if (inflight) {
      return inflight;
    }

    const request = apiClient
      .get(path, { responseType: 'blob' })
      .then((response) => {
        const blob: Blob = response.data;
        blobCache.set(path, { blob, refCount: 1 });
        inflightRequests.delete(path);
        return blob;
      })
      .catch((err) => {
        inflightRequests.delete(path);
        throw err;
      });

    inflightRequests.set(path, request);
    return request;
  }

  /**
   * Release a cached blob entry. Called when an image component unmounts.
   */
  releaseCachedBlob(url: string): void {
    const path = url.startsWith('http') ? new URL(url).pathname : url;
    const cached = blobCache.get(path);
    if (cached) {
      cached.refCount--;
      if (cached.refCount <= 0) {
        blobCache.delete(path);
      }
    }
  }
}

export const imageApi = new ImageApi();
